import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SchoolLayout from '../../components/SchoolLayout';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Search, User, Plus, Eye, QrCode, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  'Active': { bg: 'bg-[#F0FFF4]', text: 'text-[#15803D]', border: 'border-green-200' },
  'Inactive': { bg: 'bg-[#F7F7F7]', text: 'text-[#777]', border: 'border-[#E5E5E5]' },
  'Suspended': { bg: 'bg-[#FEF2F2]', text: 'text-[#D71920]', border: 'border-red-200' },
  'Deceased': { bg: 'bg-black/5', text: 'text-black/60', border: 'border-black/20' },
  'Transferred': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
};

const MEMBER_STATUSES = ['Active', 'Inactive', 'Suspended', 'Deceased', 'Transferred'];

const Members = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status_filter', statusFilter);
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      const [membersRes, schoolRes] = await Promise.all([
        axios.get(`${API_URL}/members?${params.toString()}`),
        axios.get(`${API_URL}/schools/${user.school_id}`)
      ]);
      setMembers(membersRes.data);
      setSchool(schoolRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, user.school_id]);

  useEffect(() => {
    const t = setTimeout(() => { fetchData(); }, 300);
    return () => clearTimeout(t);
  }, [fetchData]);

  const resetFilters = () => { setSearchTerm(''); setStatusFilter(''); };
  const hasActiveFilters = searchTerm || statusFilter;
  const canRegisterMembers = school?.status === 'Active';
  const generateQRUrl = (token) => `${process.env.REACT_APP_BACKEND_URL}/verify?token=${token}`;

  return (
    <SchoolLayout>
      <div className="space-y-5 max-w-6xl">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-xs font-bold text-[#D4AF37] uppercase tracking-widest mb-1">Portal Perguruan</div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B]" data-testid="page-title">Senarai Ahli</h1>
            <p className="text-sm text-[#777] mt-0.5">Urus ahli perguruan anda</p>
          </div>
          {canRegisterMembers && (
            <button
              onClick={() => navigate('/school/members/new')}
              className="flex items-center gap-2 bg-[#D71920] hover:bg-[#b8161c] text-white text-sm font-semibold px-4 py-2 rounded transition-colors self-start sm:self-auto"
              data-testid="add-member-button"
            >
              <Plus size={14} /> Daftar Ahli Baru
            </button>
          )}
        </div>

        <div className="h-px bg-[#E5E5E5]" />

        {!canRegisterMembers && (
          <div className="bg-[#FFFBF0] border-l-4 border-[#D4AF37] p-4 rounded text-sm text-[#7a6200] font-medium">
            Perguruan perlu berstatus Active untuk mendaftar ahli baru.
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded border border-[#E5E5E5] p-4 space-y-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
            <Input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Cari nama, no ahli atau IC..."
              className="pl-9 border-[#E5E5E5] text-sm"
              data-testid="search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {['', ...MEMBER_STATUSES].map(s => (
              <button
                key={s || 'all'}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-xs font-semibold rounded border transition-colors ${
                  statusFilter === s
                    ? 'bg-[#0B0B0B] text-white border-[#0B0B0B]'
                    : 'bg-white text-[#555] border-[#E5E5E5] hover:border-[#0B0B0B]'
                }`}
                data-testid={`filter-status-${s || 'all'}`}
              >
                {s || 'Semua Status'}
              </button>
            ))}
            {hasActiveFilters && (
              <button onClick={resetFilters} className="flex items-center gap-1 text-xs text-[#D71920] font-semibold hover:underline ml-auto" data-testid="reset-filters-button">
                <X size={12} /> Reset
              </button>
            )}
          </div>
        </div>

        {/* Members Cards */}
        {loading ? (
          <div className="text-center py-16 text-sm text-[#999] animate-pulse">Memuatkan ahli...</div>
        ) : members.length === 0 ? (
          <div className="text-center py-16 bg-white rounded border border-[#E5E5E5]">
            <User size={36} className="text-[#E5E5E5] mx-auto mb-3" />
            <p className="text-sm text-[#777] mb-4">{hasActiveFilters ? 'Tiada ahli dijumpai' : 'Belum ada ahli didaftarkan'}</p>
            {canRegisterMembers && !hasActiveFilters && (
              <button
                onClick={() => navigate('/school/members/new')}
                className="flex items-center gap-2 bg-[#D71920] hover:bg-[#b8161c] text-white text-sm font-semibold px-4 py-2 rounded transition-colors mx-auto"
              >
                <Plus size={14} /> Daftar Ahli Pertama
              </button>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {members.map((member) => {
              const sc = statusConfig[member.status] || statusConfig['Inactive'];
              return (
                <div
                  key={member.id}
                  className="bg-white rounded border border-[#E5E5E5] hover:border-[#D71920]/30 hover:shadow-md transition-all"
                  data-testid={`member-card-${member.id}`}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-10 h-10 rounded-full bg-[#D71920]/8 flex items-center justify-center flex-shrink-0">
                          <User size={16} className="text-[#D71920]" />
                        </div>
                        <div>
                          <p className="font-mono text-[10px] text-[#D71920] font-bold">{member.member_no}</p>
                          <span className={`inline-block mt-0.5 px-2 py-0.5 rounded text-[10px] font-bold border ${sc.bg} ${sc.text} ${sc.border}`}>
                            {member.status}
                          </span>
                        </div>
                      </div>
                    </div>
                    <h3 className="font-bold text-[#0B0B0B] text-sm mb-3 leading-tight">{member.full_name}</h3>
                    <div className="space-y-1.5 text-xs text-[#777] mb-3">
                      <div className="flex justify-between">
                        <span>IC:</span><span className="font-mono">{member.ic_masked}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Jantina:</span><span>{member.gender}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Telefon:</span><span>{member.phone}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-3 border-t border-[#F7F7F7]">
                      <button
                        onClick={() => navigate(`/school/members/${member.id}`)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded border border-[#E5E5E5] text-xs font-semibold text-[#D71920] hover:border-[#D71920]/30 hover:bg-[#FEF2F2]/30 transition-colors"
                        data-testid={`view-member-${member.id}`}
                      >
                        <Eye size={13} /> Lihat
                      </button>
                      <button
                        onClick={() => window.open(generateQRUrl(member.qr_token), '_blank')}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded border border-[#E5E5E5] text-xs font-semibold text-[#555] hover:border-[#D4AF37]/50 hover:bg-[#FFFBF0] transition-colors"
                        data-testid={`qr-member-${member.id}`}
                      >
                        <QrCode size={13} /> QR
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="text-center text-xs text-[#999]" data-testid="members-count">
          {members.length} ahli
        </div>
      </div>
    </SchoolLayout>
  );
};

export default Members;
